import React, { useMemo, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { PlayIcon, StopCircleIcon, SearchIcon } from 'lucide-react';
import { beneficiaries, Beneficiary } from '../data/beneficiaries';
// Mock vessel data for BLC lookup
const vesselData: Record<string, {
  name: string;
  type: string;
  regNo: string;
  owner: string;
  length: string;
  breadth: string;
  depth: string;
  weightCapacity: string;
  engineType: string;
  enginePower: string;
  status: string;
}> = {
  'VSL-001': {
    name: 'MV Sundarban-1',
    type: 'Boat',
    regNo: 'REG-2024-001',
    owner: 'Rahim Uddin',
    length: '32',
    breadth: '8',
    depth: '4',
    weightCapacity: '5.2',
    engineType: 'Diesel',
    enginePower: '120 HP',
    status: 'Active'
  },
  'VSL-002': {
    name: 'FV Padma-3',
    type: 'Trawler',
    regNo: 'REG-2023-089',
    owner: 'Karim Sheikh',
    length: '45',
    breadth: '12',
    depth: '6',
    weightCapacity: '12.5',
    engineType: 'Diesel',
    enginePower: '250 HP',
    status: 'Active'
  },
  'VSL-003': {
    name: 'BT Meghna-7',
    type: 'Launch',
    regNo: 'REG-2024-015',
    owner: 'Abdul Hakim',
    length: '55',
    breadth: '14',
    depth: '7',
    weightCapacity: '20.0',
    engineType: 'Diesel',
    enginePower: '350 HP',
    status: 'Active'
  }
};
export function HarvestManagement() {
  const [activeTab, setActiveTab] = useState<'applications' | 'apply' | 'tracking' | 'history' | 'startTrip'>('applications');
  const [currentStep, setCurrentStep] = useState(0);
  // Beneficiary search state
  const [beneficiarySearch, setBeneficiarySearch] = useState('');
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<Beneficiary | null>(null);
  const filteredBeneficiaries = useMemo(() => {
    if (!beneficiarySearch.trim()) return [];
    const term = beneficiarySearch.toLowerCase();
    return beneficiaries.filter(b => b.name.toLowerCase().includes(term) || b.nid.includes(term) || b.phone.includes(term)).slice(0, 5);
  }, [beneficiarySearch]);
  // Apply Permit Form State
  const [applyForm, setApplyForm] = useState({
    permitType: '',
    duration: '',
    crewCount: '',
    forestZone: '',
    purpose: '',
    blcNo: ''
  });
  const selectedVessel = applyForm.blcNo ? vesselData[applyForm.blcNo] : null;
  // Start Trip Form State
  const [startTripForm, setStartTripForm] = useState({
    permitId: '',
    startLocation: '',
    startDate: new Date().toISOString().split('T')[0],
    startTime: new Date().toTimeString().slice(0, 5)
  });
  // Data states
  const [applications, setApplications] = useState([{
    permitId: 'HRV-2024-001',
    beneficiaryName: 'মোঃ আঃ গনি গাজী',
    type: 'Fishing',
    appliedDate: '2024-03-28',
    duration: '7 days',
    status: 'pending' as const
  }, {
    permitId: 'HRV-2024-002',
    beneficiaryName: 'আলাউদ্দীন শেখ',
    type: 'Honey Collection',
    appliedDate: '2024-03-27',
    duration: '14 days',
    status: 'approved' as const
  }, {
    permitId: 'HRV-2024-003',
    beneficiaryName: 'পরিমল ঢালী',
    type: 'Wood Collection',
    appliedDate: '2024-03-26',
    duration: '10 days',
    status: 'approved' as const
  }]);
  const [activeTrips, setActiveTrips] = useState([{
    name: 'আলাউদ্দীন শেখ',
    vessel: 'MV Sundarban-1',
    zone: 'Chandpai Range',
    entryTime: '2024-03-30 06:00',
    elapsed: '4h 30m'
  }, {
    name: 'পরিমল ঢালী',
    vessel: 'FV Padma-3',
    zone: 'Sarankhola Range',
    entryTime: '2024-03-30 05:30',
    elapsed: '5h 00m'
  }]);
  const [tripHistory, setTripHistory] = useState([{
    tripId: 'TRIP-HRV-001',
    name: 'মোঃ আঃ গনি গাজী',
    vessel: 'BT Meghna-7',
    zone: 'Chandpai Range',
    entry: '2024-03-29 06:00',
    exit: '2024-03-29 18:30',
    duration: '12h 30m',
    status: 'approved' as const
  }]);
  const approvedPermits = applications.filter(p => p.status === 'approved');
  const selectedPermit = approvedPermits.find(p => p.permitId === startTripForm.permitId);
  const applicationColumns = [{
    key: 'permitId',
    label: 'Permit ID'
  }, {
    key: 'beneficiaryName',
    label: 'Beneficiary Name'
  }, {
    key: 'type',
    label: 'Type'
  }, {
    key: 'appliedDate',
    label: 'Applied Date'
  }, {
    key: 'duration',
    label: 'Duration'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-1176" data-mp="26v4dsb0ah4qm" />
  }];
  const historyColumns = [{
    key: 'tripId',
    label: 'Trip ID'
  }, {
    key: 'name',
    label: 'Beneficiary'
  }, {
    key: 'vessel',
    label: 'Vessel'
  }, {
    key: 'zone',
    label: 'Zone'
  }, {
    key: 'entry',
    label: 'Entry Time'
  }, {
    key: 'exit',
    label: 'Exit Time'
  }, {
    key: 'duration',
    label: 'Duration'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-1177" data-mp="3lsqf2akeop0w" />
  }];
  const filters = [{
    key: 'type',
    label: 'Permit Type',
    options: ['Fishing', 'Honey Collection', 'Wood Collection']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Pending', 'Approved', 'Rejected']
  }];
  const wizardSteps = [{
    title: 'Select Beneficiary',
    description: 'Search and select beneficiary'
  }, {
    title: 'Permit Details',
    description: 'Enter permit information'
  }, {
    title: 'Vessel & BLC',
    description: 'Enter BLC number for vessel info'
  }, {
    title: 'Review & Submit',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-1178" data-mp="294ucu7tdzdk0">
      <div data-id="element-1179" data-mp="250moibqubzmb">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1180" data-mp="250wgu5zcyiv6">Harvest Management</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-1181" data-mp="20a6butuugi10">
          Manage harvest permits (fishing, honey, wood) and track trips
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-1182" data-mp="1v98vmmte9y25">
        <div className="flex gap-4" data-id="element-1183" data-mp="1qcidzv1xyuem">
          <button onClick={() => setActiveTab('applications')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'applications' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1184" data-mp="1qdjr052ipqyz">
            Harvest Applications
          </button>
          <button onClick={() => setActiveTab('apply')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'apply' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1185" data-mp="3bq4qdd8sjw9m">
            Apply for Harvest Permit
          </button>
          <button onClick={() => setActiveTab('tracking')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'tracking' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1186" data-mp="3j7jc3ken5oqw">
            Trip Tracking ({activeTrips.length})
          </button>
          <button onClick={() => setActiveTab('history')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'history' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1187" data-mp="309q96i40peti">
            Trip History
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'applications' && <DataTable columns={applicationColumns} data={applications} filters={filters} onDataChange={setApplications} searchPlaceholder="Search harvest permits..." data-id="element-1188" data-mp="ojnz90v95gym" />}

      {activeTab === 'apply' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      if (!selectedBeneficiary || !applyForm.permitType) {
        alert('Please select a beneficiary and permit type.');
        return;
      }
      const newPermit = {
        permitId: `HRV-2024-${String(applications.length + 1).padStart(3, '0')}`,
        beneficiaryName: selectedBeneficiary.name,
        type: applyForm.permitType,
        appliedDate: new Date().toISOString().split('T')[0],
        duration: applyForm.duration ? `${applyForm.duration} days` : 'Not specified',
        status: 'pending' as const
      };
      setApplications([newPermit, ...applications]);
      alert('Harvest permit application submitted successfully!');
      setCurrentStep(0);
      setActiveTab('applications');
      // Reset form
      setApplyForm({
        permitType: '',
        duration: '',
        crewCount: '',
        forestZone: '',
        purpose: '',
        blcNo: ''
      });
      setSelectedBeneficiary(null);
      setBeneficiarySearch('');
    }} data-id="element-1189" data-mp="1kbslob42a6c3">
          {currentStep === 0 && <div className="space-y-4" data-id="element-1190" data-mp="20xkxincfyfmv">
              <div data-id="element-1191" data-mp="1mwqny1dnq8mm">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1192" data-mp="1mxiid8viq0bb">
                  Search Beneficiary *
                </label>
                <div className="relative" data-id="element-1193" data-mp="1rxo468f3wslh">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-1194" data-mp="3ha8bvphz57i6" />
                  <input type="text" placeholder="Enter NID, Name, or Phone" value={beneficiarySearch} onChange={e => {
              setBeneficiarySearch(e.target.value);
              if (selectedBeneficiary) setSelectedBeneficiary(null);
            }} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1195" data-mp="1eus7nd53mew9" />
                </div>
              </div>

              {/* Search Results */}
              {!selectedBeneficiary && beneficiarySearch.trim() && <div className="space-y-2" data-id="element-1196" data-mp="1omo9mzab2p5r">
                  {filteredBeneficiaries.length > 0 ? filteredBeneficiaries.map((b, idx) => <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-[#1B5E20] transition-colors" data-id="element-1197" data-mp="d4dd62qlp0k3" data-mpi={idx}>
                        <div className="flex items-center gap-4" data-id="element-1198" data-mp="ajff6oo1fkfd">
                          <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-xl" data-id="element-1199" data-mp="5mfbulikovry">
                            {b.photo}
                          </div>
                          <div className="flex-1" data-id="element-1200" data-mp="emtyppb6qixk">
                            <h4 className="font-semibold text-gray-900" data-id="element-1201" data-mp="endeazdd4v8f">
                              {b.name}
                            </h4>
                            <div className="text-sm text-gray-600 mt-0.5 flex gap-4" data-id="element-1202" data-mp="3j121kgl5rc3d">
                              <span data-id="element-1203" data-mp="mswa5f9v3fz">NID: {b.nid}</span>
                              <span data-id="element-1204" data-mp="odr7w5i0oed">Phone: {b.phone}</span>
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-1205" data-mp="3s0owcjjdtjng">
                              {b.profession} • {b.village}
                            </div>
                          </div>
                          <button type="button" onClick={() => {
                setSelectedBeneficiary(b);
                setBeneficiarySearch(b.name);
              }} className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-1206" data-mp="2zbrm93pbqt5s">
                            Select
                          </button>
                        </div>
                      </div>) : <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center text-sm text-gray-500" data-id="element-1207" data-mp="p93uvijk47uj">
                      No beneficiaries found matching "{beneficiarySearch}"
                    </div>}
                </div>}

              {/* Selected Beneficiary */}
              {selectedBeneficiary && <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-1208" data-mp="29pqjiljs12ar">
                  <div className="flex items-center gap-4" data-id="element-1209" data-mp="29rl7y9u9bcjl">
                    <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center text-2xl" data-id="element-1210" data-mp="3u6hnkqvcqnc9">
                      {selectedBeneficiary.photo}
                    </div>
                    <div className="flex-1" data-id="element-1211" data-mp="3lefpv0co3l5o">
                      <div className="flex items-center gap-2" data-id="element-1212" data-mp="3ld4kiw3kq5cg">
                        <h4 className="font-semibold text-gray-900" data-id="element-1213" data-mp="3gfl53sv53mix">
                          {selectedBeneficiary.name}
                        </h4>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-1214" data-mp="36igjbu57ht2n">
                          Selected
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1 grid grid-cols-2 gap-1" data-id="element-1215" data-mp="2xik1abdbnsgk">
                        <div data-id="element-1216" data-mp="10pi6zz9h3z3s">NID: {selectedBeneficiary.nid}</div>
                        <div data-id="element-1217" data-mp="10mvxvv6xpbjg">Phone: {selectedBeneficiary.phone}</div>
                        <div data-id="element-1218" data-mp="vqovudhnsk6s">Profession: {selectedBeneficiary.profession}</div>
                        <div data-id="element-1219" data-mp="3oloxafh1cr92">Village: {selectedBeneficiary.village}</div>
                      </div>
                    </div>
                    <button type="button" onClick={() => {
              setSelectedBeneficiary(null);
              setBeneficiarySearch('');
            }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium" data-id="element-1220" data-mp="3tixiyq1e7df2">
                      Change
                    </button>
                  </div>
                </div>}
            </div>}

          {currentStep === 1 && <div className="space-y-4" data-id="element-1221" data-mp="30u9qoai87ns0">
              <div data-id="element-1222" data-mp="30syqeknzoy5n">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1223" data-mp="2mva4d9i6rkz0">
                  Permit Type *
                </label>
                <select value={applyForm.permitType} onChange={e => setApplyForm({
            ...applyForm,
            permitType: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1224" data-mp="2rtbnfufm3kah">
                  <option value="" data-id="element-1225" data-mp="2d638cij6mut8">Select Type</option>
                  <option value="Fishing" data-id="element-1226" data-mp="2r6xhx4hyv1th">Fishing</option>
                  <option value="Honey Collection" data-id="element-1227" data-mp="2r5mbsy11thz7">Honey Collection</option>
                  <option value="Wood Collection" data-id="element-1228" data-mp="2m7kx0pano4tj">Wood Collection</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1229" data-mp="30wh007xuurna">
                <div data-id="element-1230" data-mp="2vxvzs8ils7yu">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1231" data-mp="2vzgt5ut6lkv4">
                    Requested Duration (Days) *
                  </label>
                  <input type="number" value={applyForm.duration} onChange={e => setApplyForm({
              ...applyForm,
              duration: e.target.value
            })} placeholder="e.g. 7" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1232" data-mp="35wliu4m8jygo" />
                </div>
                <div data-id="element-1233" data-mp="3voztk23wjq0l">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1234" data-mp="3vprn77dxvdo8">
                    Number of Crew *
                  </label>
                  <input type="number" value={applyForm.crewCount} onChange={e => setApplyForm({
              ...applyForm,
              crewCount: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1235" data-mp="9j2x1e1abh2j" />
                </div>
              </div>
              <div data-id="element-1236" data-mp="sn6asj8ablun">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1237" data-mp="smmu15udf3i7">
                  Forest Zone/Area *
                </label>
                <select value={applyForm.forestZone} onChange={e => setApplyForm({
            ...applyForm,
            forestZone: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1238" data-mp="ojgndx3cjab7">
                  <option value="" data-id="element-1239" data-mp="12dr7g14eegax">Select Zone</option>
                  <option value="Chandpai Range" data-id="element-1240" data-mp="12czopqvwgd22">Chandpai Range</option>
                  <option value="Sarankhola Range" data-id="element-1241" data-mp="3s3cpib5z5pi4">Sarankhola Range</option>
                  <option value="Khulna Range" data-id="element-1242" data-mp="3n2xdpo1ccq29">Khulna Range</option>
                  <option value="Satkhira Range" data-id="element-1243" data-mp="3n5tbncd6veqp">Satkhira Range</option>
                </select>
              </div>
              <div data-id="element-1244" data-mp="1vz5aawgu9mwe">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1245" data-mp="1vzor29ur658u">
                  Purpose
                </label>
                <textarea rows={3} value={applyForm.purpose} onChange={e => setApplyForm({
            ...applyForm,
            purpose: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1246" data-mp="1h3pbke5bd2to"></textarea>
              </div>
            </div>}

          {currentStep === 2 && <div className="space-y-6" data-id="element-1247" data-mp="2spo0s7mn39t0">
              <div data-id="element-1248" data-mp="2soml1puohz4y">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1249" data-mp="2socwm6pa801d">
                  BLC No. *
                </label>
                <input type="text" placeholder="e.g. VSL-001" value={applyForm.blcNo} onChange={e => setApplyForm({
            ...applyForm,
            blcNo: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1250" data-mp="m1f8qjydz7vv" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-1251" data-mp="34e3qz4u17rx">
                  Enter a valid BLC number to retrieve vessel information. Try
                  VSL-001, VSL-002, VSL-003.
                </p>
              </div>

              {selectedVessel && <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1252" data-mp="80urhs84dk4h">
                  <h4 className="font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2" data-id="element-1253" data-mp="1nhmt52roq94x">
                    Vessel Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-1254" data-mp="1jj5nyortr8q5">
                    <div data-id="element-1255" data-mp="18afqt80gi6qg">
                      <span className="text-gray-600 block mb-1" data-id="element-1256" data-mp="18bqvrb5n1kj5">
                        Vessel Name
                      </span>
                      <span className="font-medium" data-id="element-1257" data-mp="1i8mxmy1178eq">{selectedVessel.name}</span>
                    </div>
                    <div data-id="element-1258" data-mp="1d8r27n6egpas">
                      <span className="text-gray-600 block mb-1" data-id="element-1259" data-mp="1d7fnwtfbvz5f">
                        Vessel Type
                      </span>
                      <span className="font-medium" data-id="element-1260" data-mp="1r6fbryv0oa0j">{selectedVessel.type}</span>
                    </div>
                    <div data-id="element-1261" data-mp="1n5djk1zioy82">
                      <span className="text-gray-600 block mb-1" data-id="element-1262" data-mp="20z4kilz6n9s6">
                        Registration No
                      </span>
                      <span className="font-medium" data-id="element-1263" data-mp="1w01sq387r025">
                        {selectedVessel.regNo}
                      </span>
                    </div>
                    <div data-id="element-1264" data-mp="25xxxmdak4yrt">
                      <span className="text-gray-600 block mb-1" data-id="element-1265" data-mp="25yqy4wixas1i">Owner</span>
                      <span className="font-medium" data-id="element-1266" data-mp="2piy9oxmho0k4">
                        {selectedVessel.owner}
                      </span>
                    </div>
                    <div data-id="element-1267" data-mp="2tk9yj0xjghr9">
                      <span className="text-gray-600 block mb-1" data-id="element-1268" data-mp="2flac7zxi0ey9">
                        Dimensions (L×B×D)
                      </span>
                      <span className="font-medium" data-id="element-1269" data-mp="2kjbx8qehiocd">
                        {selectedVessel.length}' × {selectedVessel.breadth}' ×{' '}
                        {selectedVessel.depth}'
                      </span>
                    </div>
                    <div data-id="element-1270" data-mp="2aobktp57ym3r">
                      <span className="text-gray-600 block mb-1" data-id="element-1271" data-mp="1wv2fq0ob0mr1">
                        Engine Info
                      </span>
                      <span className="font-medium" data-id="element-1272" data-mp="21uyi5vhcs64h">
                        {selectedVessel.engineType} (
                        {selectedVessel.enginePower})
                      </span>
                    </div>
                  </div>
                </div>}
            </div>}

          {currentStep === 3 && <div className="space-y-6" data-id="element-1273" data-mp="18t76jrhgadpz">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1274" data-mp="13zul1q82in1g">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-1275" data-mp="13yb3zxel87ym">
                  Beneficiary
                </h4>
                <div className="text-sm" data-id="element-1276" data-mp="u282mtrk8dcq">
                  {selectedBeneficiary ? <div className="grid grid-cols-2 gap-2" data-id="element-1277" data-mp="u0dez7owm74y">
                      <div data-id="element-1278" data-mp="1roneng17wisw">
                        <span className="text-gray-600" data-id="element-1279" data-mp="1rnvud1d2m7hx">Name:</span>{' '}
                        <span className="font-medium" data-id="element-1280" data-mp="1mreyfkzmqoua">
                          {selectedBeneficiary.name}
                        </span>
                      </div>
                      <div data-id="element-1281" data-mp="1ct7mjgk76wz5">
                        <span className="text-gray-600" data-id="element-1282" data-mp="1crwidfmubn7i">NID:</span>{' '}
                        <span className="font-medium" data-id="element-1283" data-mp="6cvt1ssbm39w">
                          {selectedBeneficiary.nid}
                        </span>
                      </div>
                      <div data-id="element-1284" data-mp="1fvk9a436mf3">
                        <span className="text-gray-600" data-id="element-1285" data-mp="2d1e7yv6bnw8i">Phone:</span>{' '}
                        <span className="font-medium" data-id="element-1286" data-mp="2hzz0e8fc1bme">
                          {selectedBeneficiary.phone}
                        </span>
                      </div>
                      <div data-id="element-1287" data-mp="2mxr2twdlsg4u">
                        <span className="text-gray-600" data-id="element-1288" data-mp="2rpsat63qnkp2">Profession:</span>{' '}
                        <span className="font-medium" data-id="element-1289" data-mp="2rrvayiyluu7i">
                          {selectedBeneficiary.profession}
                        </span>
                      </div>
                    </div> : <span className="text-gray-500" data-id="element-1290" data-mp="35pjw7rwl43d3">
                      No beneficiary selected
                    </span>}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1291" data-mp="3amjzjv21us0i">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-1292" data-mp="1bck81ae520u7">
                  Permit Details
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-1293" data-mp="1gavklsvtipg4">
                  <div data-id="element-1294" data-mp="36qxtbi3h5dcx">
                    <span className="text-gray-600" data-id="element-1295" data-mp="36q5vs1qbh5k0">Type:</span>{' '}
                    <span className="font-medium" data-id="element-1296" data-mp="31tfifm5wco2a">
                      {applyForm.permitType || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-1297" data-mp="3fq2oceftuokc">
                    <span className="text-gray-600" data-id="element-1298" data-mp="3bqagy5266ogk">Duration:</span>{' '}
                    <span className="font-medium" data-id="element-1299" data-mp="3boz8vt1oyujn">
                      {applyForm.duration ? `${applyForm.duration} days` : 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-1300" data-mp="2wtt8blld7nxp">
                    <span className="text-gray-600" data-id="element-1301" data-mp="2wv4694s4q1gw">Zone:</span>{' '}
                    <span className="font-medium" data-id="element-1302" data-mp="2s2kt28xct8cm">
                      {applyForm.forestZone || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-1303" data-mp="20bn3veu745vw">
                    <span className="text-gray-600" data-id="element-1304" data-mp="20c6nr13ep4ck">Crew:</span>{' '}
                    <span className="font-medium" data-id="element-1305" data-mp="209u5emtqkryz">
                      {applyForm.crewCount ? `${applyForm.crewCount} persons` : 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1306" data-mp="1g7fualzzcdt2">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-1307" data-mp="1l9oqrsc29xzx">
                  Vessel Information
                </h4>
                <div className="text-sm" data-id="element-1308" data-mp="28yr4ip3kr6p5">
                  {selectedVessel ? <div className="grid grid-cols-2 gap-3" data-id="element-1309" data-mp="2dvzpexg3xou3">
                      <div data-id="element-1310" data-mp="1z0a9nh1im7xt">
                        <span className="text-gray-600" data-id="element-1311" data-mp="1yz0aspmu8vq4">BLC No:</span>{' '}
                        <span className="font-medium" data-id="element-1312" data-mp="23yll1w7gkqi5">{applyForm.blcNo}</span>
                      </div>
                      <div data-id="element-1313" data-mp="nkd9ewo881la">
                        <span className="text-gray-600" data-id="element-1314" data-mp="nk3kzdity2hp">Vessel Name:</span>{' '}
                        <span className="font-medium" data-id="element-1315" data-mp="sk7z4ww9ml4r">
                          {selectedVessel.name}
                        </span>
                      </div>
                      <div data-id="element-1316" data-mp="2v8g4kcn9n5jr">
                        <span className="text-gray-600" data-id="element-1317" data-mp="2q70iqid9kv0b">Type:</span>{' '}
                        <span className="font-medium" data-id="element-1318" data-mp="2lgtu4iir5ai2">
                          {selectedVessel.type}
                        </span>
                      </div>
                      <div data-id="element-1319" data-mp="2gh7fc4n9zm75">
                        <span className="text-gray-600" data-id="element-1320" data-mp="2gfwb63px4cfi">Owner:</span>{' '}
                        <span className="font-medium" data-id="element-1321" data-mp="2bjywe1uaze4u">
                          {selectedVessel.owner}
                        </span>
                      </div>
                    </div> : <span className="text-gray-500" data-id="element-1322" data-mp="22k22nlt23p2x">No vessel selected</span>}
                </div>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'tracking' && <div className="space-y-4" data-id="element-1323" data-mp="3rx5khxfc1o33">
          <div className="flex justify-end" data-id="element-1324" data-mp="3ry7811djc31p">
            <button onClick={() => setActiveTab('startTrip')} className="flex items-center gap-2 px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32]" data-id="element-1325" data-mp="3rv2rr5mspguo">
              <PlayIcon size={16} data-id="element-1326" data-mp="d0u1phqgr709" />
              Start New Trip
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1327" data-mp="hzx753acvf8r">
            {activeTrips.map((trip, idx) => <div key={idx} className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1328" data-mp="3gicbvvtpkq3u" data-mpi={idx}>
                <div className="flex items-center justify-between mb-3" data-id="element-1329" data-mp="3lgvw9rsnltu3">
                  <h4 className="font-semibold text-gray-900" data-id="element-1330" data-mp="3qexfqdtzrv63">{trip.name}</h4>
                  <div className="flex items-center gap-1" data-id="element-1331" data-mp="3vfctv7m3l6p4">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" data-id="element-1332" data-mp="3vcgt7biv83wz"></div>
                    <span className="text-xs text-green-600 font-medium" data-id="element-1333" data-mp="15rlwcarvrnlh">
                      In Forest
                    </span>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-gray-600" data-id="element-1334" data-mp="1vt6bgit7re4i">
                  <div data-id="element-1335" data-mp="1qu213oujzacl">
                    <span className="font-medium" data-id="element-1336" data-mp="1quv023n9svk6">Vessel:</span> {trip.vessel}
                  </div>
                  <div data-id="element-1337" data-mp="25j80bcte50kh">
                    <span className="font-medium" data-id="element-1338" data-mp="25kj2x9b3o2a0">Zone:</span> {trip.zone}
                  </div>
                  <div data-id="element-1339" data-mp="20pmzld0exshl">
                    <span className="font-medium" data-id="element-1340" data-mp="2ej4hktdgax5i">Entry:</span> {trip.entryTime}
                  </div>
                  <div data-id="element-1341" data-mp="2aht1pfkdpqai">
                    <span className="font-medium" data-id="element-1342" data-mp="2aiato000dicm">Elapsed:</span> {trip.elapsed}
                  </div>
                </div>
                <button className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50" data-id="element-1343" data-mp="2ogsf1r2fyird">
                  <StopCircleIcon size={16} data-id="element-1344" data-mp="u2ri5bqgc5tx" />
                  End Trip
                </button>
              </div>)}
          </div>
        </div>}

      {activeTab === 'startTrip' && <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-3xl mx-auto" data-id="element-1345" data-mp="30r9xgf8d80t2">
          <div className="flex items-center justify-between mb-6 border-b border-gray-200 pb-4" data-id="element-1346" data-mp="35r612dd5hq83">
            <div data-id="element-1347" data-mp="2r29wwre7sxcr">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-1348" data-mp="2r0p3j53mzkgh">
                Start Harvest Trip
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-1349" data-mp="2vz8ph5i8cw8u">
                Select an approved permit and provide trip details
              </p>
            </div>
            <div className="text-right" data-id="element-1350" data-mp="2n0oc8jvfves0">
              <div className="text-sm text-gray-500" data-id="element-1351" data-mp="2n1pzdmpqbrq3">
                Trip ID (Auto-generated)
              </div>
              <div className="font-mono font-bold text-lg text-[#1B5E20]" data-id="element-1352" data-mp="284yyta4bk9yw">
                TRP-HRV-
                {Math.floor(Math.random() * 10000).toString().padStart(4, '0')}
              </div>
            </div>
          </div>

          <form onSubmit={e => {
        e.preventDefault();
        alert('Trip started successfully! Status updated to Ongoing.');
        setActiveTab('tracking');
      }} className="space-y-6" data-id="element-1353" data-mp="b9azb511yd72">
            {/* Select Approved Permit */}
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1354" data-mp="1lj4nipi0ygfj">
              <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1355" data-mp="1qbo9oaus2jvy">
                Select Approved Permit *
              </h3>
              <div data-id="element-1356" data-mp="1vc276yyr6bfq">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1357" data-mp="208kbjxq687rj">
                  Permit
                </label>
                <select required value={startTripForm.permitId} onChange={e => setStartTripForm({
              ...startTripForm,
              permitId: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1358" data-mp="24bgvbrrg69x2">
                  <option value="" data-id="element-1359" data-mp="2l8eb7qk1vnr">Select an approved permit</option>
                  {approvedPermits.map(p => <option key={p.permitId} value={p.permitId} data-id="element-1360" data-mp="3tr01by91cs6c">
                      {p.permitId} — {p.beneficiaryName} ({p.type})
                    </option>)}
                </select>
              </div>

              {/* Permit Details Card */}
              {selectedPermit && <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-1361" data-mp="lbgaf5vu7lug">
                  <div className="flex items-center justify-between" data-id="element-1362" data-mp="gc3nkl4yuerv">
                    <h4 className="font-semibold text-gray-900" data-id="element-1363" data-mp="gehfwm89hcvq">
                      Permit Details
                    </h4>
                    <StatusBadge status={selectedPermit.status} data-id="element-1364" data-mp="313dodzdx4b6t" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-1365" data-mp="2x1iliqgc9pqx">
                    <div data-id="element-1366" data-mp="2x3daqgyn840t">
                      <span className="text-gray-600" data-id="element-1367" data-mp="2yipe228xgq4m">Beneficiary:</span>{' '}
                      <span className="font-medium" data-id="element-1368" data-mp="2yglcvs2ey581">
                        {selectedPermit.beneficiaryName}
                      </span>
                    </div>
                    <div data-id="element-1369" data-mp="2ojgnljd9ttn0">
                      <span className="text-gray-600" data-id="element-1370" data-mp="2omckd4dduc9v">Type:</span>{' '}
                      <span className="font-medium" data-id="element-1371" data-mp="2ti1mnfgse79v">{selectedPermit.type}</span>
                    </div>
                    <div data-id="element-1372" data-mp="2fqdetodkm4yp">
                      <span className="text-gray-600" data-id="element-1373" data-mp="2fq3lprx8bhos">Duration:</span>{' '}
                      <span className="font-medium" data-id="element-1374" data-mp="2kk7t2caeprqe">
                        {selectedPermit.duration}
                      </span>
                    </div>
                    <div data-id="element-1375" data-mp="2asbwj5nyy9oa">
                      <span className="text-gray-600" data-id="element-1376" data-mp="2atmyczxut7cr">Applied Date:</span>{' '}
                      <span className="font-medium" data-id="element-1377" data-mp="1wsu0c58cfu4w">
                        {selectedPermit.appliedDate}
                      </span>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Journey Details */}
            {selectedPermit && <div className="space-y-4" data-id="element-1378" data-mp="1d8c03pvk5f5m">
                <h3 className="font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-1379" data-mp="1d9ddi101qdqi">
                  Journey Details
                </h3>
                <div data-id="element-1380" data-mp="1m7p9kijom3qc">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1381" data-mp="1m777h5ac8vai">
                    Start Location (Station) *
                  </label>
                  <select required value={startTripForm.startLocation} onChange={e => setStartTripForm({
              ...startTripForm,
              startLocation: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1382" data-mp="1w6egjga1i3rt">
                    <option value="" data-id="element-1383" data-mp="2yyqpl4dvqunn">Select Station</option>
                    <option value="chandpai" data-id="element-1384" data-mp="2yy8voeeowsiw">Chandpai Station</option>
                    <option value="katka" data-id="element-1385" data-mp="32zke9zz5cdhl">Katka Station</option>
                    <option value="dhangmari" data-id="element-1386" data-mp="37r2f8o166e2k">Dhangmari Station</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-1387" data-mp="3cqou11wnc2dh">
                  <div data-id="element-1388" data-mp="3cs9nspb4zhaa">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1389" data-mp="2fzwdy49pqabp">
                      Start Date *
                    </label>
                    <input type="date" required value={startTripForm.startDate} onChange={e => setStartTripForm({
                ...startTripForm,
                startDate: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1390" data-mp="2k29gykx2ru4s" />
                  </div>
                  <div data-id="element-1391" data-mp="1oso4bu49qmvh">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1392" data-mp="1otflhzx4ci28">
                      Start Time *
                    </label>
                    <input type="time" required value={startTripForm.startTime} onChange={e => setStartTripForm({
                ...startTripForm,
                startTime: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1393" data-mp="1jzbejgnusa15" />
                  </div>
                </div>
              </div>}

            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200" data-id="element-1394" data-mp="2qhqet053rbht">
              <button type="button" onClick={() => setActiveTab('tracking')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium" data-id="element-1395" data-mp="2qfx7dimo0n8r">
                Cancel
              </button>
              <button type="submit" disabled={!selectedPermit} className="px-6 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1396" data-mp="ew7r3f2va1oe">
                <PlayIcon size={18} data-id="element-1397" data-mp="1chn1nil824cl" />
                Start Trip
              </button>
            </div>
          </form>
        </div>}

      {activeTab === 'history' && <DataTable columns={historyColumns} data={tripHistory} onDataChange={setTripHistory} searchPlaceholder="Search trip history..." data-id="element-1398" data-mp="12ixuajqyc2al" />}
    </div>;
}